import {
  API_ROOT as DEFAULT_APP_API_ROOT,
  GA_TRACKING_ID,
  ODDLE_PASS_CLIENT_ID,
  origin
} from 'config/client'
import { isBrowser } from '@jarvis.oddle/core/env'
import { postLogApi } from '@jarvis.oddle/core/api/newrelic/client'

import NextRouter from 'next/router'
import { getFullProductDetails, getOrderItemQuantity } from 'src/utils/product'
import { isObjectEmpty } from 'src/utils/object'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite, selectIsBot } from 'src/redux/meta/selector'
import { parseJwt } from 'src/utils/jwtParser'

const name = 'Oddle Shop'
const safeGtag = (command, targetId, config) =>
  isBrowser() && window.gtag && window.gtag(command, targetId, config)
const safeFbq = (command, targetId, config?) =>
  isBrowser() && window.fbq && window.fbq(command, targetId, config)

const safeSelector = (attribute, attributeName) => {
  try {
    const element = document.querySelector(
      `[${attribute}="${attributeName}"]`
    ) as HTMLMetaElement
    return element.content
  } catch (e) {
    return undefined
  }
}

const safeSegment = (callback) => {
  if (!isBrowser() || !window.analytics) {
    postLogApi({
      req: undefined,
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      payload: { message: 'Segment is not ready' }
    })
    return
  }
  callback(window.analytics)
}

export type UseTracker = {
  notifyPageView: () => void
  notifyViewContent: (productId?: string) => void
  notifyInitiateCheckout: () => void
  notifyPurchase: () => void
  segmentTrackerCheckoutStarted: (payload: {
    data: {
      segment: Record<string, unknown>
    }
  }) => void
  segmentTrackerSignedOut: (payload) => void
  segmentTrackerSignedIn: (payload) => void
  segmentTrackerMenuViewed: () => void
  segmentTrackerOrderReminderSaved: (payload: Record<string, any>) => void
  segmentTrackerOrderedShare: (payload: Record<string, any>) => void
  SegmentTrackerOrderViewed: (payload: Record<string, any>) => void
  notifyAddToCart: (payload) => void
  notifyNewsletterCompleteRegistration: (payload) => void
  notifyNewsletterLead: () => void
  notifyAccountCompleteRegistration: (payload) => void
  notifyAccountLead: () => void
  notifyViewCart: (storeOder: Oms.V4.StoreOrder) => void
  notifyLogin: (method: string) => void
}

const useTracker = (): UseTracker => {
  const state = useAppSelector((state) => state)
  const meta = useAppSelector(selectDetailMenuSite)
  const isBot = useAppSelector(selectIsBot)

  const notifyPageView = () => {
    try {
      const ga = (state['@meta'].trackers.data || []).find(
        (t) => t.trackingClient === 'GoogleAnalytics'
      )
      let customerId
      if (
        typeof window !== 'undefined' &&
        window.localStorage.getItem('accessToken')
      ) {
        ;({ customerId } = parseJwt(window.localStorage.getItem('accessToken')))
      }
      ga &&
        ga.trackingId &&
        safeGtag('config', ga.trackingId, {
          page_location: window.location.href,
          ...(customerId
            ? {
                user_id: customerId
              }
            : {})
        })
      GA_TRACKING_ID.split(';').map((tag) => {
        safeGtag('config', tag, {
          page_location: window.location.href,
          ...(customerId
            ? {
                user_id: customerId
              }
            : {})
        })
      })
      safeFbq('track', 'PageView')
      // trigger page viewed for segment
      if (meta?.customerSegmentEnabled) {
        let item
        // check this condition to detect customer has accessed to production details route
        if (NextRouter.router.query.productId) {
          const id = NextRouter.router.query.productId
          const menu = state['@menu'].data || []
          for (const cat of menu) {
            item = cat?.items?.find((catItem) => catItem.id === id)
            if (item) break
          }
        }
        safeSegment((analytics) =>
          analytics.page(
            undefined,
            undefined,
            {
              url: window.location.href,
              // sometime document.title don't have the name of production
              title: item && item.name ? item.name : document.title,
              path: window.location.pathname,
              referrer: document.referrer,
              country: meta.menuCountry,
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name
              },
              accountId: meta?.account?.id,
              accountName: meta?.account?.name,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name
            },
            {
              context: { groupId: meta?.brand?.id }
            }
          )
        )
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Page View` }
        })
      }
    }
  }

  const notifyViewContent = (productId) => {
    try {
      const id = productId || NextRouter.router.query.productId
      const menu = state['@menu'].data || []
      const { currencyCode } = state['@meta'].detailMenuSite.data
      let category, item
      for (const cat of menu) {
        item = cat?.items?.find?.((catItem) => catItem.id === id)
        if (item) {
          category = cat
          break
        }
      }

      if (item) {
        safeFbq('track', 'ViewContent', {
          value: item.variants?.[0]?.price,
          currency: meta.currencyCode,
          content_name: item.name,
          content_type: 'product',
          content_ids: [item.id]
        })

        const viewContentItemPayload = {
          item_id: item.id,
          item_name: item.name,
          item_brand: meta.menu.name,
          item_category: category.name,
          quantity: 1,
          price: item.variants?.[0]?.price
        }
        safeGtag('event', 'view_item', {
          items: [viewContentItemPayload],
          currency: currencyCode,
          value: item.variants?.[0]?.price
        })
        if (meta.customerSegmentEnabled) {
          safeSegment((analytics) =>
            analytics.track(
              'Product Viewed',
              {
                app: {
                  id: ODDLE_PASS_CLIENT_ID,
                  name
                },
                productId: viewContentItemPayload.item_id,
                category: viewContentItemPayload.item_category,
                name: viewContentItemPayload.item_name,
                brand: viewContentItemPayload.item_brand,
                price: viewContentItemPayload.price,
                quantity: viewContentItemPayload.quantity,
                value:
                  viewContentItemPayload.price *
                  viewContentItemPayload.quantity,
                currency: meta.currencyCode,
                menuId: meta.menu.id,
                accountId: meta.menu?.account?.id,
                url: window.location.href,
                ...(item.imageFullUrls?.[0] ||
                  (item.imageUrls?.[0] && {
                    imageUrl: `${meta.imagePath}product/${
                      item.imageFullUrls?.[0] || item.imageUrls?.[0]
                    }.jpg`
                  })),
                country: meta.menuCountry,
                accountName: meta?.account?.name,
                brandId: meta?.brand?.id,
                brandName: meta?.brand?.name
              },
              {
                context: { groupId: meta?.brand?.id }
              }
            )
          )
        }
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at View Content` }
        })
      }
    }
  }

  const notifyInitiateCheckout = () => {
    try {
      if (isBot) return

      const menu = state['@menu'].data
      const storeOrderData =
        state['@checkout'].meta.data?.storeOrder || ({} as Oms.V4.StoreOrder)
      const { currencyCode } = state['@meta'].detailMenuSite.data

      const orderItems =
        state['@checkout'].meta.data?.storeOrder?.orderItems ||
        ([] as Oms.V4.OrderItem[])
      const fullDetailedOrderItems = orderItems.map((order) => ({
        order,
        product: getFullProductDetails(menu, order)
      }))
      safeFbq('track', 'InitiateCheckout', {
        value: storeOrderData.total,
        currency: storeOrderData.currencyCode,
        num_items: getOrderItemQuantity(orderItems),
        contents: fullDetailedOrderItems.map(({ order, product }) => ({
          id: product.itemId,
          quantity: order.quantity,
          item_price: order.price
        }))
      })
      const checkoutPayload = fullDetailedOrderItems.map(
        ({ order, product }) => ({
          item_id: product.itemId,
          item_name: order.name,
          item_brand: meta.menu.name,
          item_category: (
            menu.find((category) =>
              category.items.find((item) => item.id === product.itemId)
            ) || {}
          ).name,
          quantity: order.quantity,
          price: order.price
        })
      )
      safeGtag('event', 'begin_checkout', {
        items: checkoutPayload,
        currency: currencyCode,
        value: storeOrderData.total
      })
    } catch (ex) {
      console.log(ex)
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Initiate Checkout` }
        })
      }
    }
  }

  const notifyPurchase = () => {
    try {
      if (
        state['@thankyou'].checkoutResults.status !== 'fulfilled' &&
        state['@checkout'].checkoutResults.status !== 'fulfilled'
      ) {
        return
      }
      if (isBot) return

      const storeOrderData = !isObjectEmpty(
        state['@checkout'].checkoutResults.data
      )
        ? state['@checkout'].checkoutResults.data.storeOrder
        : state['@thankyou'].checkoutResults.data.storeOrder
      const taxAndCharges = storeOrderData.taxAndCharges || []
      const orderItems = storeOrderData.orderItems || []
      const menu = state['@menu'].data
      safeFbq('track', 'Purchase', {
        value: storeOrderData.total,
        currency: storeOrderData.currencyCode,
        num_items: getOrderItemQuantity(orderItems),
        contents: orderItems.map((order) => {
          const productDetails = getFullProductDetails(menu, order)
          return {
            id: productDetails.itemId,
            quantity: order.quantity,
            item_price: order.price
          }
        })
      })
      const purchasePayload = {
        transaction_id: storeOrderData.id,
        affiliation: storeOrderData.menuName,
        value: storeOrderData.total,
        shipping: storeOrderData.deliveryCost,
        tax: taxAndCharges.reduce((sum, t) => sum + t.amount, 0),
        currency: storeOrderData.currencyCode,
        items: orderItems.map((order) => ({
          item_id: order.itemId,
          item_name: order.name,
          quantity: order.quantity,
          price: order.price,
          item_brand: meta.menu.name,
          item_category: (
            menu.find((category) =>
              category.items.find((item) => item.id === order.itemId)
            ) || {}
          ).name
        }))
      }
      safeGtag('event', 'purchase', purchasePayload)
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Purchase` }
        })
      }
    }
  }

  const segmentTrackerCheckoutStarted = (payload) => {
    try {
      if (isBot) return
      if (!isObjectEmpty(payload?.data?.segment || {})) {
        payload.data.segment.oddlePassFirstOrder = undefined
        payload.data.segment.oddlePassFirstMenuOrder = undefined
        payload.data.segment.submittedDate = undefined
        payload.data.segment.orderNumber = undefined
        payload.data.segment.oddleEatsReferrer = undefined
        payload.data.segment.value = payload.data.segment.total
        payload.data.segment.total = undefined
        safeSegment((analytics) =>
          analytics.track(
            'Checkout Started',
            {
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name
              },
              country: meta.menuCountry,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name,
              ...payload.data.segment
            },
            {
              context: { groupId: meta?.brand?.id }
            }
          )
        )
      } else {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: {
            message: `Missed segment data at Checkout Started: ${JSON.stringify(
              payload
            )}`
          }
        })
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Checkout Started` }
        })
      }
    }
  }

  const segmentTrackerSignedOut = ({ country }) => {
    const meta = state['@meta'].detailMenuSite
    if (meta.data.customerSegmentEnabled) {
      safeSegment((analytics) => {
        analytics.track(
          'Signed Out',
          {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name
            },
            country,
            accountId: meta.data?.account?.id,
            accountName: meta.data?.account?.name,
            brandId: meta.data?.brand?.id,
            brandName: meta.data?.brand?.name
          },
          {
            context: { groupId: meta.data?.brand?.id }
          }
        )
        analytics.reset()
      })
    }
  }

  const segmentTrackerSignedIn = (payload) => {
    try {
      const meta = state['@meta'].detailMenuSite
      if (meta.data.customerSegmentEnabled) {
        const {
          id,
          email,
          firstName,
          lastName,
          phone,
          continueWithOddlePass,
          country,
          phoneIsVerified,
          authMethod
        } = payload
        if (payload.action !== 'verification') {
          safeSegment((analytics) => {
            analytics.identify(id, {
              email,
              firstName,
              lastName,
              phone,
              country,
              phoneIsVerified
            })
            analytics.track(
              'Signed In',
              {
                app: {
                  id: ODDLE_PASS_CLIENT_ID,
                  name
                },
                email,
                firstName,
                lastName,
                phone,
                continueWithOddlePass,
                country,
                accountId: meta.data?.account?.id,
                accountName: meta.data?.account?.name,
                brandId: meta.data?.brand?.id,
                brandName: meta.data?.brand?.name,
                phoneIsVerified,
                authMethod
              },
              {
                context: { groupId: meta.data?.brand?.id }
              }
            )
          })
        }
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Signed In` }
        })
      }
    }
  }

  const segmentTrackerMenuViewed = () => {
    try {
      if (meta?.customerSegmentEnabled) {
        safeSegment((analytics) =>
          analytics.track(
            'Menu Viewed',
            {
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name
              },
              id: meta.menu.id,
              name: meta.menu.name,
              url: window.location.href,
              country: meta.menuCountry,
              accountId: meta?.account?.id,
              accountName: meta?.account?.name,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name
            },
            {
              context: { groupId: meta?.brand?.id }
            }
          )
        )
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Menu viewed` }
        })
      }
    }
  }

  const notifyAddToCart = ({ payload }) => {
    try {
      const menu = state['@menu'].data
      const category = menu.find((category) =>
        category.items.find((item) => item.id === payload.menuItem.id)
      )
      safeFbq('track', 'AddToCart', {
        value: payload.orderItem.quantity * payload.finalPrice,
        currency: meta.currencyCode,
        content_name: payload.menuItem.name,
        content_type: 'product',
        contents: [
          {
            id: payload.menuItem.id,
            quantity: payload.orderItem.quantity,
            item_price: payload.finalPrice
          }
        ]
      })
      const addToCartItemPayload = {
        item_id: payload.menuItem.id,
        item_name: payload.menuItem.name,
        item_brand: meta.menu.name,
        item_category: category.name,
        quantity: payload.orderItem.quantity,
        price: payload.finalPrice
      }
      safeGtag('event', 'add_to_cart', {
        items: [addToCartItemPayload],
        currency: meta.currencyCode,
        value: payload.finalPrice
      })
      if (meta.customerSegmentEnabled) {
        safeSegment((analytics) =>
          analytics.track(
            'Product Added',
            {
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name
              },
              productId: addToCartItemPayload.item_id,
              category: addToCartItemPayload.item_category,
              name: addToCartItemPayload.item_name,
              brand: addToCartItemPayload.item_brand,
              price: addToCartItemPayload.price,
              quantity: addToCartItemPayload.quantity,
              value: addToCartItemPayload.price * addToCartItemPayload.quantity,
              currency: meta.currencyCode,
              menuId: meta.menu.id,
              accountId: meta.menu?.account?.id,
              url: window.location.href,
              ...(payload.menuItem.imageFullUrls?.[0] ||
                (payload.imageUrls?.[0] && {
                  imageUrl: `${meta.imagePath}product/${
                    payload.menuItem.imageFullUrls?.[0] ||
                    payload.menuItem.imageUrls?.[0]
                  }.jpg`
                })),
              country: meta.menuCountry,
              accountName: meta?.account?.name,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name
            },
            {
              context: { groupId: meta?.brand?.id }
            }
          )
        )
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Add to cart` }
        })
      }
    }
  }

  const notifyNewsletterCompleteRegistration = (payload) => {
    safeFbq('track', 'CompleteRegistration', { content_name: 'newsletter' })
    safeGtag('event', 'complete_registration', {
      event_category: 'engagement',
      event_label: 'newsletter'
    })
  }

  const notifyNewsletterLead = () => {
    safeFbq('track', 'Lead', { content_name: 'newsletter' })
    safeGtag('event', 'lead', {
      event_category: 'engagement',
      event_label: 'newsletter'
    })
  }

  // For non Oddle Pass shop, it is impossible to differentiate between Facebook login or registration since we use the same API and the whole process is invisible to Frontend
  const notifyAccountCompleteRegistration = (payload) => {
    safeFbq('track', 'CompleteRegistration', { content_name: 'account' })
    safeGtag('event', 'sign_up', {
      method: payload.facebookId ? 'facebook' : 'email'
    })
  }

  const notifyAccountLead = () => {
    safeFbq('track', 'Lead', { content_name: 'account' })
    safeGtag('event', 'lead', {
      event_category: 'engagement',
      event_label: 'account'
    })
  }

  const segmentTrackerOrderReminderSaved = (payload) => {
    try {
      const products = (payload.orderItems || []).map((item) => {
        return {
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          id: item.id,
          orderTags: item.subitemChoices
            ? item.subitemChoices.map((x) => ({
                id: x.name,
                name: x.name,
                price: x.price,
                amount: Number((x.price * x.quantity).toFixed(2)),
                quantity: x.quantity
              }))
            : null
        }
      })
      const amount = {
        charges: (payload.taxAndCharges || [])
          .filter((item) => !item.description.includes('GST'))
          .reduce((sum, t) => sum + t.amount, 0),
        discount: (payload.orderPromotions || []).reduce(
          (sum, t) => sum + t.amount,
          0
        ),
        tax:
          (payload.taxAndCharges || []).filter((item) =>
            item.description.includes('GST')
          )?.[0]?.amount || 0,
        taxAndCharges: (payload.taxAndCharges || []).reduce(
          (sum, t) => sum + t.amount,
          0
        ),
        amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0)
      }
      if (meta?.customerSegmentEnabled) {
        safeSegment((analytics) =>
          analytics.track('Order Reminder Saved', {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name
            },
            orderNumber: payload.orderNumber,
            isPickup: payload.isPickup,
            submittedDate: payload.orderDate,
            accountId: meta.menu?.account?.id,
            accountName: meta.menu.email,
            menuId: meta.menu.id,
            menuName: meta.menu.name,
            storeId: payload.storeId,
            storeName: payload.storeName,
            promoCode: payload?.orderPromotions
              ? payload?.orderPromotions?.[0]?.promoCode
              : '',
            orderId: payload.id,
            charge: amount.charges,
            delivery: payload.deliveryCost,
            deliveryWithTaxCharge: amount.taxAndCharges + payload.deliveryCost,
            discount: amount.discount,
            discountWithTaxCharge: amount.discount + amount.tax,
            subTotal: payload.subtotal,
            subTotalWithCharge: payload.subtotal + amount.tax,
            tax: amount.tax,
            total: payload.total,
            currency: payload.currencyCode,
            products: {
              productItems: products,
              amount: amount.amountProducts
            },
            orderReminderDestination: payload.orderReminderDestination
          })
        )
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: {
            message: `Tracker error ${ex} at TrackerOrderReminderSaved`
          }
        })
      }
    }
  }

  const segmentTrackerOrderedShare = (payload) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const products = (payload.orderItems || []).map((item) => {
          return {
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            id: item.id,
            orderTags: item.subitemChoices
              ? item.subitemChoices.map((x) => ({
                  id: x.name,
                  name: x.name,
                  price: x.price,
                  amount: Number((x.price * x.quantity).toFixed(2)),
                  quantity: x.quantity
                }))
              : null
          }
        })
        const amount = {
          charges: (payload.taxAndCharges || [])
            .filter((item) => !item.description.includes('GST'))
            .reduce((sum, t) => sum + t.amount, 0),
          discount: (payload.orderPromotions || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          tax:
            (payload.taxAndCharges || []).filter((item) =>
              item.description.includes('GST')
            )?.[0]?.amount || 0,
          taxAndCharges: (payload.taxAndCharges || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0)
        }
        safeSegment((analytics) =>
          analytics.track('Order Shared', {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name
            },
            orderNumber: payload.orderNumber,
            isPickup: payload.isPickup,
            submittedDate: payload.orderDate,
            accountId: meta.menu?.account?.id,
            accountName: meta.menu.email,
            menuId: meta.menu.id,
            menuName: meta.menu.name,
            storeId: payload.storeId,
            storeName: payload.storeName,
            promoCode: payload?.orderPromotions
              ? payload?.orderPromotions[0].promoCode
              : '',
            orderId: payload.id,
            charge: amount.charges,
            delivery: payload.deliveryCost,
            deliveryWithTaxCharge: amount.taxAndCharges + payload.deliveryCost,
            discount: amount.discount,
            discountWithTaxCharge: amount.discount + amount.tax,
            subTotal: payload.subtotal,
            subTotalWithCharge: payload.subtotal + amount.tax,
            tax: amount.tax,
            total: payload.total,
            currency: payload.currencyCode,
            products: {
              productItems: products,
              amount: amount.amountProducts
            },
            orderShareDestination: payload.appName
          })
        )
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at TrackerOrderedShare` }
        })
      }
    }
  }

  const SegmentTrackerOrderViewed = (payload) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const products = (payload.orderItems || []).map((item) => {
          return {
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            id: item.id
          }
        })
        const amount = {
          charges: (payload.taxAndCharges || [])
            .filter((item) => !item.description.includes('GST'))
            .reduce((sum, t) => sum + t.amount, 0),
          discount: (payload.orderPromotions || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          tax:
            (payload.taxAndCharges || []).filter((item) =>
              item.description.includes('GST')
            )?.[0]?.amount || 0,
          taxAndCharges: (payload.taxAndCharges || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0)
        }
        safeSegment((analytics) =>
          analytics.track(
            'Order Viewed',
            {
              isPurchaser: payload.isPurchaser,
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name
              },
              orderNumber: payload.orderNumber,
              isPickup: payload.isPickup,
              submittedDate: payload.orderDate,
              accountId: meta.menu?.account?.id,
              accountName: meta.menu.email,
              menuId: meta.menu.id,
              menuName: meta.menu.name,
              storeId: payload.storeId,
              storeName: payload.storeName,
              promoCode: payload?.orderPromotions
                ? payload?.orderPromotions[0].name
                : '',
              orderId: payload.id,
              charge: amount.charges,
              delivery: payload.deliveryCost,
              deliveryWithTaxCharge:
                amount.taxAndCharges + payload.deliveryCost,
              discount: amount.discount,
              discountWithTaxCharge: amount.discount + amount.tax,
              subTotal: payload.subtotal,
              subTotalWithCharge: payload.subtotal + amount.tax,
              tax: amount.tax,
              total: payload.total,
              currency: payload.currencyCode,
              products: {
                productItems: products,
                amount: amount.amountProducts
              },
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name
            },
            {
              context: { groupId: meta?.brand?.id }
            }
          )
        )
      }
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at TrackerOrderViewed` }
        })
      }
    }
  }

  const notifyViewCart = (storeOrderData: Oms.V4.StoreOrder) => {
    try {
      const menu = state['@menu'].data
      const payload = {
        currency: storeOrderData.currencyCode,
        value: storeOrderData.total,
        items: storeOrderData.orderItems?.map((orderItem) => ({
          item_id: orderItem.itemId,
          item_name: orderItem.name,
          quantity: orderItem.quantity,
          price: orderItem.price,
          item_brand: meta.menu.name,
          item_category: (
            menu.find((category) =>
              category.items.find((item) => item.id === orderItem.itemId)
            ) || {}
          ).name
        }))
      }
      safeGtag('event', 'view_cart', payload)
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at View cart` }
        })
      }
    }
  }

  const notifyLogin = (method: string) => {
    try {
      safeGtag('event', 'login', { method })
    } catch (ex) {
      if (typeof window !== 'undefined') {
        postLogApi({
          req: undefined,
          origin,
          apiRoot: DEFAULT_APP_API_ROOT,
          payload: { message: `Tracker error ${ex} at Login` }
        })
      }
    }
  }

  return {
    notifyPageView,
    notifyViewContent,
    notifyInitiateCheckout,
    notifyPurchase,
    segmentTrackerCheckoutStarted,
    segmentTrackerSignedOut,
    segmentTrackerSignedIn,
    segmentTrackerMenuViewed,
    segmentTrackerOrderReminderSaved,
    segmentTrackerOrderedShare,
    SegmentTrackerOrderViewed,
    notifyAddToCart,
    notifyNewsletterCompleteRegistration,
    notifyNewsletterLead,
    notifyAccountCompleteRegistration,
    notifyAccountLead,
    notifyViewCart,
    notifyLogin
  }
}

export default useTracker
